import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  notificacoesApi,
  type Notificacao,
  type SeveridadeNotificacao,
} from '@/lib/api/core';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';
import { formatarData } from '@/lib/format';

const CHIP: Record<SeveridadeNotificacao, string> = {
  critica: 'crit',
  atencao: 'warn',
  informativa: 'neutral',
  positiva: 'good',
};

const ROTULO: Record<SeveridadeNotificacao, string> = {
  critica: 'Crítica',
  atencao: 'Atenção',
  informativa: 'Informativa',
  positiva: 'Positiva',
};

export function NotifPage() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();

  const [apenasNaoLidas, setApenasNaoLidas] = useState(false);
  const [tipoFiltro, setTipoFiltro] = useState('');

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const { data: notificacoes = [], isLoading } = useQuery({
    queryKey: ['notificacoes', apenasNaoLidas],
    queryFn: () => notificacoesApi.list(token, apenasNaoLidas),
    enabled: !!token,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['notificacoes'] });

  const gerar = useMutation({
    mutationFn: () => notificacoesApi.gerar(token),
    onSuccess: (r) => {
      invalidar();
      toast(
        r.criadas > 0
          ? `${r.criadas} nova(s) notificação(ões) a partir do estado atual.`
          : 'Nenhum fato novo desde a última verificação.',
      );
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao verificar notificações.')),
  });

  const marcar = useMutation({
    mutationFn: ({ id, lida }: { id: string; lida: boolean }) =>
      notificacoesApi.marcarLida(id, lida, token),
    onSuccess: invalidar,
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar a notificação.')),
  });

  const marcarTodas = useMutation({
    mutationFn: () => notificacoesApi.marcarTodasLidas(token),
    onSuccess: (r) => {
      invalidar();
      toast(`${r.atualizadas} notificação(ões) marcada(s) como lida(s).`);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao marcar como lidas.')),
  });

  const remover = useMutation({
    mutationFn: (id: string) => notificacoesApi.remover(id, token),
    onSuccess: () => {
      invalidar();
      toast('Notificação removida.');
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover a notificação.')),
  });

  const tipos = [...new Set(notificacoes.map((n) => n.tipo))];
  const lista = notificacoes.filter((n) => !tipoFiltro || n.tipo === tipoFiltro);
  const naoLidas = notificacoes.filter((n) => !n.lida).length;
  const criticas = notificacoes.filter((n) => n.severidade === 'critica' && !n.lida).length;

  const marcarComoVista = (n: Notificacao) => {
    if (!n.lida) marcar.mutate({ id: n.id, lida: true });
  };

  return (
    <div className="page" id="p-notif">
      <div className="head">
        <div>
          <h1>Notificações</h1>
          <p>
            Fatos que exigem atenção, derivados do estado real do gabinete: SLA vencendo,
            aniversariantes do dia, compromissos de hoje e ações paradas.
          </p>
        </div>
        <div className="act">
          <button className="btn" onClick={() => gerar.mutate()} disabled={gerar.isPending}>
            {gerar.isPending ? 'Verificando...' : 'Verificar agora'}
          </button>
          <button
            className="btn pri"
            onClick={() => marcarTodas.mutate()}
            disabled={marcarTodas.isPending || naoLidas === 0}
          >
            Marcar todas como lidas
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Não lidas</div>
          <div className="v" style={{ color: naoLidas ? 'var(--accent)' : 'var(--ink)' }}>
            {naoLidas}
          </div>
          <div className="d">de {notificacoes.length} no total</div>
        </div>
        <div className="card kpi">
          <div className="l">Críticas abertas</div>
          <div className="v" style={{ color: criticas ? 'var(--crit)' : 'var(--good)' }}>
            {criticas}
          </div>
          <div className="d">exigem ação imediata</div>
        </div>
        <div className="card kpi">
          <div className="l">Tipos de evento</div>
          <div className="v">{tipos.length}</div>
          <div className="d">{tipos.join(' · ') || '—'}</div>
        </div>
        <div className="card kpi">
          <div className="l">Origem</div>
          <div className="v" style={{ fontSize: 18 }}>
            Estado real
          </div>
          <div className="d">nada é cadastrado manualmente aqui</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filters">
        <span
          className={`chip neutral chipbtn ${!apenasNaoLidas ? 'on' : ''}`}
          onClick={() => setApenasNaoLidas(false)}
        >
          Todas
        </span>
        <span
          className={`chip neutral chipbtn ${apenasNaoLidas ? 'on' : ''}`}
          onClick={() => setApenasNaoLidas(true)}
        >
          Só não lidas
        </span>
        <span style={{ flex: 1 }} />
        <select className="sel" value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
          <option value="">Todos os tipos</option>
          {tipos.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {/* Lista */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {lista.map((n) => {
          const estilo = {
            borderLeft: `4px solid var(--${
              n.severidade === 'critica'
                ? 'crit'
                : n.severidade === 'atencao'
                ? 'warn'
                : n.severidade === 'positiva'
                ? 'good'
                : 'line'
            })`,
            opacity: n.lida ? 0.6 : 1,
            cursor: n.link ? 'pointer' : 'default',
            display: 'block',
            textDecoration: 'none',
            color: 'inherit',
          } as const;

          const conteudo = (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div className="filters" style={{ margin: '0 0 6px' }}>
                  <span className={`chip ${CHIP[n.severidade]}`}>{ROTULO[n.severidade]}</span>
                  <span className="chip neutral">{n.tipo}</span>
                  {n.bairro && <span className="chip eleg">{n.bairro}</span>}
                  <span style={{ fontSize: 11, color: 'var(--ink3)' }}>
                    {formatarData(n.created_at)}
                  </span>
                </div>
                <b style={{ fontSize: 14 }}>{n.titulo}</b>
                {n.descricao && (
                  <p style={{ fontSize: 13, color: 'var(--ink2)', margin: '4px 0 0' }}>
                    {n.descricao}
                  </p>
                )}
              </div>

              <div
                style={{ display: 'flex', gap: 6, whiteSpace: 'nowrap' }}
                // O card virou um <Link> (react-router v7): é o próprio
                // onClick do Link quem chama preventDefault() antes de
                // navegar pelo router. Só stopPropagation() corta a
                // propagação sintética ANTES desse handler rodar, então o
                // preventDefault() nunca acontece e a ação padrão nativa da
                // âncora (navegação de página inteira) segue por conta
                // própria — daí o preventDefault() explícito aqui.
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <button
                  className="btn sm"
                  onClick={() => marcar.mutate({ id: n.id, lida: !n.lida })}
                  disabled={marcar.isPending}
                >
                  {n.lida ? 'Marcar não lida' : 'Marcar lida'}
                </button>
                <button
                  className="btn sm"
                  style={{ color: 'var(--crit)' }}
                  onClick={() => remover.mutate(n.id)}
                  disabled={remover.isPending}
                >
                  ✕
                </button>
              </div>
            </div>
          );

          return n.link ? (
            <Link
              key={n.id}
              to={n.link}
              className="card"
              style={estilo}
              onClick={() => marcarComoVista(n)}
            >
              {conteudo}
            </Link>
          ) : (
            <div key={n.id} className="card" style={estilo} onClick={() => marcarComoVista(n)}>
              {conteudo}
            </div>
          );
        })}

        {isLoading && <div className="empty">Carregando notificações...</div>}
        {!isLoading && lista.length === 0 && (
          <div className="empty">
            Nenhuma notificação. Use <b>Verificar agora</b> para reavaliar o estado do gabinete.
          </div>
        )}
      </div>
    </div>
  );
}
